import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "../components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash2, Pencil } from "lucide-react";

const empty = { name: "", email: "", phone: "", company: "", gstin: "", address: "", notes: "" };

export default function Clients() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);

  const fetchItems = async () => {
    const r = await api.get("/clients");
    setItems(r.data || []);
  };
  useEffect(() => { fetchItems(); }, []);

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (c) => { setEditing(c); setForm({ ...empty, ...c }); setOpen(true); };

  const submit = async () => {
    if (!form.name) { toast.error("Name required"); return; }
    try {
      const payload = { ...form };
      Object.keys(payload).forEach((k) => { if (payload[k] === "") delete payload[k]; });
      if (editing) {
        await api.put(`/clients/${editing.id}`, payload);
        toast.success("Client updated");
      } else {
        await api.post("/clients", payload);
        toast.success("Client added");
      }
      setOpen(false);
      fetchItems();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    }
  };

  const remove = async (id) => {
    await api.delete(`/clients/${id}`);
    toast.success("Deleted");
    fetchItems();
  };

  return (
    <div className="space-y-8" data-testid="clients-page">
      <div className="flex items-end justify-between">
        <div>
          <p className="tc-overline mb-2">Address book</p>
          <h1 className="font-['Outfit'] text-3xl md:text-4xl font-bold tracking-tight">Clients</h1>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="client-new-btn" onClick={openNew} className="bg-[#d95d39] hover:bg-[#c84c35] text-white rounded-md">
              <Plus size={16} className="mr-1" /> Add client
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl bg-white">
            <DialogHeader>
              <DialogTitle className="font-['Outfit'] text-2xl">{editing ? "Edit client" : "New client"}</DialogTitle>
            </DialogHeader>
            <div className="grid grid-cols-2 gap-4">
              {[
                ["name", "Name", true],
                ["company", "Company"],
                ["email", "Email"],
                ["phone", "Phone"],
                ["gstin", "GSTIN"],
              ].map(([k, label, req]) => (
                <div key={k}>
                  <Label className="text-xs uppercase tracking-[0.18em] font-bold text-[#5a5a53]">{label}{req ? " *" : ""}</Label>
                  <Input value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                    className="mt-2 h-11 rounded-md border-[#e5e5e0]" data-testid={`client-${k}-input`} />
                </div>
              ))}
              <div className="col-span-2">
                <Label className="text-xs uppercase tracking-[0.18em] font-bold text-[#5a5a53]">Address</Label>
                <Textarea value={form.address || ""} onChange={(e) => setForm({ ...form, address: e.target.value })}
                  className="mt-2 rounded-md border-[#e5e5e0]" rows={2} />
              </div>
              <div className="col-span-2">
                <Label className="text-xs uppercase tracking-[0.18em] font-bold text-[#5a5a53]">Notes</Label>
                <Textarea value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  className="mt-2 rounded-md border-[#e5e5e0]" rows={2} />
              </div>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
              <Button data-testid="client-submit" onClick={submit} className="bg-[#0a0a0a] hover:bg-[#1a1a1a] text-white rounded-md">
                {editing ? "Save" : "Add client"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="tc-card overflow-hidden overflow-x-auto">
        {items.length === 0 ? (
          <div className="p-16 text-center text-sm text-[#5a5a53]" data-testid="clients-empty">
            No clients yet. Add your first client to start invoicing.
          </div>
        ) : (
          <table className="w-full text-sm min-w-[640px]" data-testid="clients-table">
            <thead className="bg-[#f4f1eb] text-[10px] tracking-[0.18em] uppercase text-[#5a5a53]">
              <tr>
                <th className="text-left px-6 py-3 font-bold">Name</th>
                <th className="text-left px-6 py-3 font-bold">Company</th>
                <th className="text-left px-6 py-3 font-bold">Email</th>
                <th className="text-left px-6 py-3 font-bold">GSTIN</th>
                <th className="px-6 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((c) => (
                <tr key={c.id} className="border-t border-[#e5e5e0] hover:bg-[#fafaf8]">
                  <td className="px-6 py-3 font-medium">{c.name}</td>
                  <td className="px-6 py-3 text-[#5a5a53]">{c.company || "—"}</td>
                  <td className="px-6 py-3 text-[#5a5a53]">{c.email || "—"}</td>
                  <td className="px-6 py-3 font-mono text-xs">{c.gstin || "—"}</td>
                  <td className="px-6 py-3 text-right">
                    <div className="flex justify-end gap-3">
                      <button data-testid={`client-edit-${c.id}`} onClick={() => openEdit(c)} className="text-[#5a5a53] hover:text-[#0a0a0a]">
                        <Pencil size={15} />
                      </button>
                      <button data-testid={`client-delete-${c.id}`} onClick={() => remove(c.id)} className="text-[#5a5a53] hover:text-[#b83a26]">
                        <Trash2 size={15} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
